import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiClient, TokenManager } from '../../services/api';
import OTPService from '../../services/otpService';
import ParticleBackground from '../../components/common/ParticleBackground';

const VerifyOTPPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Get email, name, password, fromLogin, fromRegistration, collegeId, and branch from location state
  const { email, name, password, fromLogin, fromRegistration, returnTo, message: stateMessage, collegeId, branch } = location.state || {};

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Email is missing. Please try registering again.');
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const response = await apiClient.post('/auth/verify-otp', {
        email,
        otp
      });

      if (response.success) {
        // Handle both registration and login flows
        const userData = response.data?.user;
        const token = response.data?.token;

        // Store token for automatic login if provided
        if (token) {
          TokenManager.setToken(token);
          // Store user data with all required fields
          if (userData) {
            localStorage.setItem('user_data', JSON.stringify({
              id: userData.id,
              name: userData.name,
              email: userData.email,
              role: userData.role,
              branch: userData.branch,
              collegeId: userData.collegeId,
              collegeName: userData.collegeName,
              emailVerified: true, // Email is now verified
              isVerified: userData.isVerified, // Management approval status
              profilePicture: userData.profilePicture,
              mobileNumber: userData.mobileNumber
            }));
          }
        }

        if (fromRegistration || fromLogin) {
          // After registration or login - check if we have user data
          if (!userData) {
            // If no user data in response, try to get from token
            setMessage('Email verified successfully! Redirecting...');
            setTimeout(() => {
              window.location.href = '/';
            }, 800);
            return;
          }

          // Check if management has approved the account (isVerified)
          if (userData.isVerified === true) {
            // Account is approved by management - redirect to clubs
            setMessage('Email verified successfully! Redirecting to your dashboard...');
            setTimeout(() => {
              // Force reload to update auth context
              window.location.href = '/clubs';
            }, 800);
          } else {
            // Email verified but account not yet approved by management
            // Show student approval page
            setMessage('Email verified! Your account is pending management approval...');
            setTimeout(() => {
              window.location.href = '/pending-approval';
            }, 800);
          }
        } else {
          // Regular flow - redirect to home
          setMessage('Email verified successfully! Redirecting...');
          setTimeout(() => {
            window.location.href = '/';
          }, 800);
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to verify OTP. Please try again.';

      // Check if error is "Email is already verified"
      if (errorMessage.toLowerCase().includes('already verified')) {
        setMessage(errorMessage + ' Redirecting...');
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!email) {
      setError('Email is missing. Please try registering again.');
      return;
    }

    try {
      setResending(true);
      setError('');
      setMessage('');

      await OTPService.sendVerificationOTP(email);
      setMessage('New OTP sent to your email!');
      setCountdown(120); // 2 minutes cooldown
    } catch (err: any) {
      if (err.response?.status === 429 && err.response?.data?.remainingSeconds) {
        setCountdown(err.response.data.remainingSeconds);
        setError(err.response.data.message);
      } else {
        setError(err.response?.data?.message || 'Failed to resend OTP. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <>
        <ParticleBackground />
        <Container maxWidth="sm">
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Alert severity="error" sx={{ mb: 3 }}>
                Email address is missing. Please register again.
              </Alert>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/register')}
              >
                Go to Registration
              </Button>
            </Paper>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <ParticleBackground />
      <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            width: '100%',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: 'primary.main',
                mb: 2,
              }}
            />
            <Typography variant="h5" gutterBottom fontWeight="600">
              {fromLogin ? `Hi ${name || 'there'}!` : fromRegistration ? 'Complete Your Registration' : 'Verify Your Email'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {fromLogin
                ? 'Please verify your email to continue'
                : fromRegistration
                ? 'First, verify your email to proceed'
                : 'Verify your email address'}
            </Typography>
            {fromLogin && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Email Verification Required</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  We've sent a 6-digit verification code to <strong>{email}</strong>
                </Typography>
              </Alert>
            )}
            {!fromLogin && (
              <>
                <Typography variant="body2" color="text.secondary">
                  We've sent a 6-digit OTP to
                </Typography>
                <Typography variant="body1" fontWeight="600" color="primary.main">
                  {email}
                </Typography>
              </>
            )}
          </Box>

          {stateMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {stateMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleVerify}>
            <TextField
              fullWidth
              label="Enter OTP"
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtp(value);
              }}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }
              }}
              sx={{ mb: 3 }}
              helperText="Enter the 6-digit code sent to your email"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || otp.length !== 6}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify OTP'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Didn't receive the code?
              </Typography>
              <Button
                variant="text"
                onClick={handleResendOTP}
                disabled={resending || countdown > 0}
              >
                {resending ? (
                  <CircularProgress size={20} />
                ) : countdown > 0 ? (
                  `Resend OTP (${countdown}s)`
                ) : (
                  'Resend OTP'
                )}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate(fromRegistration ? '/register' : '/login')}
                sx={{ textDecoration: 'none' }}
              >
                {fromRegistration ? 'Back to Registration' : 'Back to Login'}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
    </>
  );
};

export default VerifyOTPPage;
